import React, { useState, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertColor,
    Autocomplete,
    Chip,
    Box,
} from "@mui/material";
import { styled } from "@mui/system";
import { Formik, Form, Field } from "formik";
import axiosInstance from "../../services/axios-instance";
import * as Yup from "yup";
import axios from "axios";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import { Organization } from "../../interface/organizationInterface";
import { Client } from "../../interface/clientInterface";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.text.primary,
}));
const StyledTableRow = styled(TableRow)(() => ({
    backgroundColor: "white",
}));

export const organizationNameSchema = Yup.object({
    name: Yup.string().required("Organization Name is required"),
});

const getTimelogs = (organizationId: number) => {
    window.location.href = "timelog/organization/" + organizationId;
};

interface TableOrganizationsProps {
    organizations: Organization[];
    setOrganizations: React.Dispatch<React.SetStateAction<Organization[]>>;
    allClients: Client[];
}

export const getAvailableClientsForEdit = (
    allClients: Client[],
    organizations: Organization[],
    editingOrgId: number | null,
    editingClients: Client[]
): Client[] => {
    const editingOrgClientIds = editingClients.map(c => c.id);
    return allClients.filter(client => {
        const isInAnotherOrg = organizations.some(
            org => org.id !== editingOrgId && org.clients?.some(c => c.id === client.id)
        );
        return !isInAnotherOrg || editingOrgClientIds.includes(client.id);
    });
};

export const getUnassignedClients = (
    allClients: Client[],
    organizations: Organization[]
): Client[] => {
    const assignedClientIds = new Set(
        organizations.flatMap(org => org.clients?.map(c => c.id) || [])
    );
    return allClients.filter(client => !assignedClientIds.has(client.id));
};

const TableOrganizations = ({ organizations, setOrganizations, allClients }: TableOrganizationsProps) => {
    const [editingOrgId, setEditingOrgId] = useState<number | null>(null);
    const [editingClients, setEditingClients] = useState<Client[]>([]);
    const [deleteOrgId, setDeleteOrgId] = useState<number | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [severity, setSeverity] = useState<AlertColor | undefined>("info");

    const formRef = useRef<any>(null);

    const handleEditClick = (org: Organization) => {
        setEditingOrgId(org.id);
        setEditingClients(org.clients || []);
    };

    const handleSaveClick = async (values: { id: number; name: string }) => {
        try {
            const response = await axiosInstance.patch(
                `/organizations/${values.id}`,
                {
                    name: values.name,
                    client_ids: editingClients.map(c => c.id),
                }
            );
            const updatedOrgs = organizations.map((org) =>
                org.id === values.id ? { ...response.data.data } : org
            );
            setOrganizations(updatedOrgs);
            setSeverity("success");
            setAlertMessage("Organization updated successfully");
            setEditingOrgId(null);
            setEditingClients([]);
        } catch (error) {
            setSeverity("error");
            setAlertMessage(
                axios.isAxiosError(error) ? error.response?.data.message : "Error updating organization"
            );
        }
    };

    const handleDeleteClick = (orgId: number) => {
        setDeleteOrgId(orgId);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axiosInstance.delete(`/organizations/${deleteOrgId}`);
            setOrganizations(organizations.filter((org) => org.id !== deleteOrgId));
            setSeverity("success");
            setAlertMessage("Organization deleted successfully");
        } catch (error) {
            setSeverity("error");
            setAlertMessage(
                axios.isAxiosError(error) ? error.response?.data.message : "Error deleting organization"
            );
        }
        setDeleteOrgId(null);
        setOpenDeleteDialog(false);
    };

    const handleDeleteCancel = () => {
        setDeleteOrgId(null);
        setOpenDeleteDialog(false);
    };

    return (
        <div>
            <TableContainer component={Paper}>
                <Box>
                    {alertMessage !== "" && (
                        <Alert
                            onClick={() => setAlertMessage("")}
                            variant="filled"
                            severity={severity}
                        >
                            {alertMessage}
                        </Alert>
                    )}
                </Box>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f9fafc" }}>
                            <StyledTableCell>ID</StyledTableCell>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Clients</StyledTableCell>
                            <StyledTableCell>Edit</StyledTableCell>
                            <StyledTableCell>Delete</StyledTableCell>
                            <StyledTableCell>Timelog</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizations.map((org) => (
                            <StyledTableRow key={org.id}>
                                <StyledTableCell>{org.id}</StyledTableCell>
                                <StyledTableCell>
                                    {editingOrgId === org.id ? (
                                        <Formik
                                            initialValues={{ id: org.id, name: org.name }}
                                            validationSchema={organizationNameSchema}
                                            onSubmit={handleSaveClick}
                                            innerRef={formRef}
                                        >
                                            {({ handleSubmit, handleChange, errors, touched }) => (
                                                <Form onSubmit={handleSubmit}>
                                                    <Field
                                                        as={TextField}
                                                        name="name"
                                                        error={touched.name && !!errors.name}
                                                        helperText={touched.name && errors.name}
                                                        onChange={handleChange}
                                                    />
                                                    <Button style={{ display: "none" }} type="submit">
                                                        Save
                                                    </Button>
                                                </Form>
                                            )}
                                        </Formik>
                                    ) : (
                                        org.name
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {editingOrgId === org.id ? (
                                        <Autocomplete
                                            multiple
                                            options={getAvailableClientsForEdit(allClients, organizations, editingOrgId, editingClients)}
                                            getOptionLabel={(option) => option.name}
                                            value={editingClients}
                                            onChange={(_, newValue) => setEditingClients(newValue)}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        label={option.name}
                                                        {...getTagProps({ index })}
                                                        key={option.id}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select clients"
                                                    size="small"
                                                />
                                            )}
                                            sx={{ minWidth: 250 }}
                                        />
                                    ) : (
                                        org.clients?.map(c => c.name).join(", ") || "—"
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {editingOrgId === org.id ? (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => formRef.current.submitForm()}
                                        >
                                            Save
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleEditClick(org)}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleDeleteClick(org.id)}
                                    >
                                        Delete
                                    </Button>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Button onClick={() => getTimelogs(org.id)}>
                                        <ExitToAppOutlinedIcon />
                                    </Button>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this organization?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TableOrganizations;
